import { clsx } from "clsx";
import type { InputHTMLAttributes, ReactNode } from "react";
import { useId } from "react";

type InputProps = {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "className">;

export const Input = ({ label, error, hint, leftIcon, className, id, ...props }: InputProps) => {
  const generatedId = useId();
  const inputId = id || generatedId;

  return (
    <div className={clsx("w-full", className)}>
      {label && (
        <label htmlFor={inputId} className="mb-2 block font-semibold text-ocean-800 text-sm">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 text-ocean-400">
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          className={clsx(
            "w-full rounded-xl border-[1.5px] bg-white/90 px-4 py-3.5 text-base text-ocean-900 transition-all duration-200",
            "placeholder:text-ocean-400",
            "focus:border-ocean-500 focus:shadow-focus focus:outline-none",
            leftIcon && "pl-10",
            error
              ? "border-error-border bg-error-bg/50 focus:border-error-border"
              : "border-ocean-200 hover:border-ocean-300",
          )}
          {...props}
        />
      </div>
      {error && <p className="mt-1.5 text-error-text text-sm">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-ocean-500 text-sm">{hint}</p>}
    </div>
  );
};
